/**
 * MODULE: domain/sales-presentations/frontend/components/canvas-layer-drawing
 * PURPOSE: Pure helpers that rasterize SlideLayer objects onto a Canvas2D context.
 *   Used by CanvasSlideRenderer when slide.layers is populated (Epic 3 canvas
 *   layers); legacy components rendering remains in CanvasSlideRenderer itself.
 * DEPENDENCIES: platform/types (Layer, isTextLayer, isImageLayer, isShapeLayer)
 * PUBLIC_API: drawLayers, drawTextLayer, drawImageLayer, drawShapeLayer,
 *   getLayerBounds, hitTestLayer, drawSelectionHandles, pointInBoundsInclusive,
 *   hitTestHandle, HandleId, HANDLE_HIT_PX, computeResizedBounds, clampBounds,
 *   cursorForHandle
 * SIDE_EFFECTS: draws onto the provided CanvasRenderingContext2D; spawns Image()
 *   for image layers (async paint on load — matches existing renderImage behavior)
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: ~330 / 400
 */

import {
  isImageLayer,
  isShapeLayer,
  isTextLayer,
  type Layer,
} from '../../../../platform/types';

const DEFAULT_FONT_FAMILY = 'Inter, sans-serif';
const DEFAULT_TEXT_COLOR = '#1F2937';
const DEFAULT_FILL_COLOR = '#E5E7EB';

/** Layer percentage rect → pixel rect on the canvas (slide logical space). */
export interface LayerBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface PixelRect extends LayerBounds {}

function toPixels(layer: Layer, slideWidth: number, slideHeight: number): PixelRect {
  return {
    x: (layer.x / 100) * slideWidth,
    y: (layer.y / 100) * slideHeight,
    width: (layer.width / 100) * slideWidth,
    height: (layer.height / 100) * slideHeight,
  };
}

// CSS vars are not understood by Canvas2D — resolve to safe fallbacks so the
// rasterized slide doesn't render as transparent/black.
function resolveColor(input: string, fallback = DEFAULT_TEXT_COLOR): string {
  if (!input) return fallback;
  if (input.includes('var(')) return fallback;
  if (input.startsWith('#') || input.startsWith('rgb') || input.startsWith('hsl')) return input;
  if (input === 'transparent' || input === 'none') return input;
  return `#${input}`;
}

function resolveFontFamily(input: string): string {
  if (!input || input.includes('var(')) return DEFAULT_FONT_FAMILY;
  return input;
}

function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const out: string[] = [];
  // Honor user-entered newlines first, then word-wrap each paragraph.
  for (const paragraph of text.split('\n')) {
    if (paragraph.length === 0) {
      out.push('');
      continue;
    }
    const words = paragraph.split(' ');
    let line = '';
    for (const word of words) {
      const probe = line ? `${line} ${word}` : word;
      if (ctx.measureText(probe).width > maxWidth && line) {
        out.push(line);
        line = word;
      } else {
        line = probe;
      }
    }
    if (line) out.push(line);
  }
  return out;
}

/**
 * Apply a 2D transform that rotates around the layer's pixel-rect center, then
 * runs the draw callback with origin translated to top-left of the rect. Used
 * so every per-type drawer can think in unrotated local coords.
 */
function withLayerTransform(
  ctx: CanvasRenderingContext2D,
  rect: PixelRect,
  rotationDeg: number,
  opacity: number,
  draw: (rect: PixelRect) => void
) {
  ctx.save();
  ctx.globalAlpha = clamp01(opacity);
  if (rotationDeg !== 0) {
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    ctx.translate(cx, cy);
    ctx.rotate((rotationDeg * Math.PI) / 180);
    draw({ x: -rect.width / 2, y: -rect.height / 2, width: rect.width, height: rect.height });
  } else {
    draw(rect);
  }
  ctx.restore();
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 1;
  return Math.min(1, Math.max(0, n));
}

// ─── Text ────────────────────────────────────────────────────────────────────

export function drawTextLayer(
  ctx: CanvasRenderingContext2D,
  layer: Layer,
  slideWidth: number,
  slideHeight: number
) {
  if (!isTextLayer(layer)) return;
  const rect = toPixels(layer, slideWidth, slideHeight);
  const c = layer.content;

  withLayerTransform(ctx, rect, layer.rotation, layer.style.opacity, (r) => {
    const fontStyle = c.fontStyle === 'italic' ? 'italic' : 'normal';
    ctx.font = `${fontStyle} ${c.fontWeight} ${c.fontSize}px ${resolveFontFamily(c.fontFamily)}`;
    ctx.fillStyle = resolveColor(c.color, DEFAULT_TEXT_COLOR);
    ctx.textBaseline = 'top';
    ctx.textAlign = c.textAlign;

    const lineHeight = c.fontSize * (c.lineHeight > 0 ? c.lineHeight : 1.4);
    const lines = wrapLines(ctx, c.value ?? '', r.width);
    const anchorX =
      c.textAlign === 'center' ? r.x + r.width / 2 : c.textAlign === 'right' ? r.x + r.width : r.x;
    let y = r.y;
    for (const line of lines) {
      if (y + lineHeight > r.y + r.height + lineHeight) break;
      ctx.fillText(line, anchorX, y);
      if (c.textDecoration === 'underline') {
        const w = ctx.measureText(line).width;
        const underlineY = y + c.fontSize + 1;
        const startX =
          c.textAlign === 'center'
            ? anchorX - w / 2
            : c.textAlign === 'right'
              ? anchorX - w
              : anchorX;
        ctx.fillRect(startX, underlineY, w, Math.max(1, Math.round(c.fontSize / 16)));
      }
      y += lineHeight;
    }
  });
}

// ─── Image ───────────────────────────────────────────────────────────────────

export function drawImageLayer(
  ctx: CanvasRenderingContext2D,
  layer: Layer,
  slideWidth: number,
  slideHeight: number,
  ImageCtor: typeof Image = Image
) {
  if (!isImageLayer(layer)) return;
  const rect = toPixels(layer, slideWidth, slideHeight);
  const c = layer.content;

  if (!c.src) {
    withLayerTransform(ctx, rect, layer.rotation, layer.style.opacity, (r) => {
      ctx.fillStyle = DEFAULT_FILL_COLOR;
      ctx.fillRect(r.x, r.y, r.width, r.height);
    });
    return;
  }

  // Image fetch is asynchronous; the onload handler paints when ready and
  // preserves the layer's rotation/opacity at paint time.
  const img = new ImageCtor();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    withLayerTransform(ctx, rect, layer.rotation, layer.style.opacity, (r) => {
      const flipScaleX = c.flipH ? -1 : 1;
      const flipScaleY = c.flipV ? -1 : 1;
      if (flipScaleX !== 1 || flipScaleY !== 1) {
        ctx.save();
        ctx.translate(r.x + r.width / 2, r.y + r.height / 2);
        ctx.scale(flipScaleX, flipScaleY);
        ctx.drawImage(img, -r.width / 2, -r.height / 2, r.width, r.height);
        ctx.restore();
      } else {
        ctx.drawImage(img, r.x, r.y, r.width, r.height);
      }
    });
  };
  img.onerror = () => {
    withLayerTransform(ctx, rect, layer.rotation, layer.style.opacity, (r) => {
      ctx.fillStyle = DEFAULT_FILL_COLOR;
      ctx.fillRect(r.x, r.y, r.width, r.height);
    });
  };
  img.src = c.src;
}

// ─── Shape ───────────────────────────────────────────────────────────────────

export function drawShapeLayer(
  ctx: CanvasRenderingContext2D,
  layer: Layer,
  slideWidth: number,
  slideHeight: number
) {
  if (!isShapeLayer(layer)) return;
  const rect = toPixels(layer, slideWidth, slideHeight);
  const c = layer.content;

  withLayerTransform(ctx, rect, layer.rotation, layer.style.opacity, (r) => {
    const fill = resolveColor(c.fill, DEFAULT_FILL_COLOR);
    const stroke = resolveColor(c.stroke, 'transparent');

    if (c.shape === 'rectangle') {
      drawRoundedRect(ctx, r, Math.max(0, c.borderRadius));
      if (fill !== 'transparent' && fill !== 'none') {
        ctx.fillStyle = fill;
        ctx.fill();
      }
      if (c.strokeWidth > 0 && stroke !== 'transparent' && stroke !== 'none') {
        ctx.lineWidth = c.strokeWidth;
        ctx.strokeStyle = stroke;
        ctx.stroke();
      }
      return;
    }

    if (c.shape === 'ellipse') {
      ctx.beginPath();
      ctx.ellipse(r.x + r.width / 2, r.y + r.height / 2, r.width / 2, r.height / 2, 0, 0, Math.PI * 2);
      if (fill !== 'transparent' && fill !== 'none') {
        ctx.fillStyle = fill;
        ctx.fill();
      }
      if (c.strokeWidth > 0 && stroke !== 'transparent' && stroke !== 'none') {
        ctx.lineWidth = c.strokeWidth;
        ctx.strokeStyle = stroke;
        ctx.stroke();
      }
      return;
    }

    if (c.shape === 'triangle') {
      ctx.beginPath();
      ctx.moveTo(r.x + r.width / 2, r.y);
      ctx.lineTo(r.x, r.y + r.height);
      ctx.lineTo(r.x + r.width, r.y + r.height);
      ctx.closePath();
      if (fill !== 'transparent' && fill !== 'none') {
        ctx.fillStyle = fill;
        ctx.fill();
      }
      if (c.strokeWidth > 0 && stroke !== 'transparent' && stroke !== 'none') {
        ctx.lineWidth = c.strokeWidth;
        ctx.strokeStyle = stroke;
        ctx.stroke();
      }
      return;
    }

    if (c.shape === 'line') {
      const thickness = Math.max(1, c.strokeWidth || 2);
      ctx.fillStyle = fill !== 'transparent' && fill !== 'none' ? fill : DEFAULT_TEXT_COLOR;
      ctx.fillRect(r.x, r.y + r.height / 2 - thickness / 2, r.width, thickness);
      return;
    }
  });
}

function drawRoundedRect(ctx: CanvasRenderingContext2D, rect: PixelRect, radius: number) {
  const r = Math.min(radius, rect.width / 2, rect.height / 2);
  ctx.beginPath();
  ctx.moveTo(rect.x + r, rect.y);
  ctx.lineTo(rect.x + rect.width - r, rect.y);
  ctx.quadraticCurveTo(rect.x + rect.width, rect.y, rect.x + rect.width, rect.y + r);
  ctx.lineTo(rect.x + rect.width, rect.y + rect.height - r);
  ctx.quadraticCurveTo(
    rect.x + rect.width,
    rect.y + rect.height,
    rect.x + rect.width - r,
    rect.y + rect.height
  );
  ctx.lineTo(rect.x + r, rect.y + rect.height);
  ctx.quadraticCurveTo(rect.x, rect.y + rect.height, rect.x, rect.y + rect.height - r);
  ctx.lineTo(rect.x, rect.y + r);
  ctx.quadraticCurveTo(rect.x, rect.y, rect.x + r, rect.y);
  ctx.closePath();
}

// ─── Hit testing & selection chrome (E6-1) ─────────────────────────────────────

/** Axis-aligned bounds in slide logical pixels from layer x/y/width/height (%). */
export function getLayerBounds(layer: Layer, slideWidth: number, slideHeight: number): LayerBounds {
  return toPixels(layer, slideWidth, slideHeight);
}

/** Inclusive edges so points on the border count as inside (E6-1 tests). */
export function pointInBoundsInclusive(x: number, y: number, b: LayerBounds): boolean {
  if (!(b.width > 0) || !(b.height > 0)) return false;
  return x >= b.x && x <= b.x + b.width && y >= b.y && y <= b.y + b.height;
}

/**
 * Bounding-rect hit test in slide logical coordinates (v1: ignores rotation).
 * Hidden layers never hit; locked layers still hit so the editor can select them.
 */
export function hitTestLayer(
  layer: Layer,
  x: number,
  y: number,
  slideWidth: number,
  slideHeight: number
): boolean {
  if (!layer.visible) return false;
  const b = getLayerBounds(layer, slideWidth, slideHeight);
  return pointInBoundsInclusive(x, y, b);
}

function layerWithDragOffsetPx(
  layer: Layer,
  dxPx: number,
  dyPx: number,
  slideWidth: number,
  slideHeight: number
): Layer {
  if (dxPx === 0 && dyPx === 0) return layer;
  return {
    ...layer,
    x: layer.x + (dxPx / slideWidth) * 100,
    y: layer.y + (dyPx / slideHeight) * 100,
  };
}

/** E6-3: eight resize handles (corners + edge midpoints). */
export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

/** Hit target larger than 6px drawn square (E6-3). */
export const HANDLE_HIT_PX = 12;

const MIN_RESIZE_W = 40;
const MIN_RESIZE_H = 20;

function handleCenters(bounds: LayerBounds): Array<[HandleId, number, number]> {
  const cx = [bounds.x, bounds.x + bounds.width / 2, bounds.x + bounds.width];
  const cy = [bounds.y, bounds.y + bounds.height / 2, bounds.y + bounds.height];
  return [
    ['nw', cx[0], cy[0]],
    ['n', cx[1], cy[0]],
    ['ne', cx[2], cy[0]],
    ['e', cx[2], cy[1]],
    ['se', cx[2], cy[2]],
    ['s', cx[1], cy[2]],
    ['sw', cx[0], cy[2]],
    ['w', cx[0], cy[1]],
  ];
}

/**
 * Hit-test resize handles in slide-px space. Corners checked before edges when regions overlap.
 * Uses HANDLE_HIT_PX square centered on each handle anchor (matches drawSelectionHandles geometry).
 */
export function hitTestHandle(bounds: LayerBounds, x: number, y: number): HandleId | null {
  const half = HANDLE_HIT_PX / 2;
  const order: HandleId[] = ['nw', 'ne', 'se', 'sw', 'n', 'e', 's', 'w'];
  const map = new Map<HandleId, [number, number]>(
    handleCenters(bounds).map(([id, hx, hy]) => [id, [hx, hy]])
  );
  for (const id of order) {
    const pt = map.get(id);
    if (!pt) continue;
    const [hx, hy] = pt;
    if (x >= hx - half && x <= hx + half && y >= hy - half && y <= hy + half) return id;
  }
  return null;
}

/** Resize cursor for a handle (E6-3). */
export function cursorForHandle(handle: HandleId | null): string {
  switch (handle) {
    case 'nw':
    case 'se':
      return 'nwse-resize';
    case 'ne':
    case 'sw':
      return 'nesw-resize';
    case 'n':
    case 's':
      return 'ns-resize';
    case 'e':
    case 'w':
      return 'ew-resize';
    default:
      return 'default';
  }
}

function applyAspectCorner(
  w: number,
  h: number,
  dx: number,
  dy: number,
  sw: number,
  sh: number,
  aspect: number
): { w: number; h: number } {
  if (sw <= 0 || sh <= 0) return { w, h };
  if (Math.abs(dx / sw) >= Math.abs(dy / sh)) {
    return { w, h: w / aspect };
  }
  return { w: h * aspect, h };
}

/**
 * Pure resize math in slide-px: corner handles resize both axes; edges one axis.
 * Shift + corner preserves aspect ratio using `start` geometry only (E6-3).
 */
export function computeResizedBounds(
  start: LayerBounds,
  handle: HandleId,
  dx: number,
  dy: number,
  shiftKey: boolean
): LayerBounds {
  const { x: sx, y: sy, width: sw, height: sh } = start;
  const aspect = sw > 0 && sh > 0 ? sw / sh : 1;

  let x = sx;
  let y = sy;
  let w = sw;
  let h = sh;

  const cornerShift = shiftKey && ['nw', 'ne', 'se', 'sw'].includes(handle);

  switch (handle) {
    case 'se': {
      w = sw + dx;
      h = sh + dy;
      if (cornerShift) {
        const r = applyAspectCorner(w, h, dx, dy, sw, sh, aspect);
        w = r.w;
        h = r.h;
      }
      x = sx;
      y = sy;
      break;
    }
    case 'nw': {
      w = sw - dx;
      h = sh - dy;
      if (cornerShift) {
        const r = applyAspectCorner(w, h, dx, dy, sw, sh, aspect);
        w = r.w;
        h = r.h;
      }
      w = Math.max(MIN_RESIZE_W, w);
      h = Math.max(MIN_RESIZE_H, h);
      x = sx + sw - w;
      y = sy + sh - h;
      return { x, y, width: w, height: h };
    }
    case 'ne': {
      w = sw + dx;
      h = sh - dy;
      if (cornerShift) {
        const r = applyAspectCorner(w, h, dx, dy, sw, sh, aspect);
        w = r.w;
        h = r.h;
      }
      w = Math.max(MIN_RESIZE_W, w);
      h = Math.max(MIN_RESIZE_H, h);
      x = sx;
      y = sy + sh - h;
      return { x, y, width: w, height: h };
    }
    case 'sw': {
      w = sw - dx;
      h = sh + dy;
      if (cornerShift) {
        const r = applyAspectCorner(w, h, dx, dy, sw, sh, aspect);
        w = r.w;
        h = r.h;
      }
      w = Math.max(MIN_RESIZE_W, w);
      h = Math.max(MIN_RESIZE_H, h);
      x = sx + sw - w;
      y = sy;
      return { x, y, width: w, height: h };
    }
    case 'n': {
      h = Math.max(MIN_RESIZE_H, sh - dy);
      w = sw;
      x = sx;
      y = sy + sh - h;
      return { x, y, width: w, height: h };
    }
    case 's': {
      h = Math.max(MIN_RESIZE_H, sh + dy);
      w = sw;
      x = sx;
      y = sy;
      break;
    }
    case 'e': {
      w = Math.max(MIN_RESIZE_W, sw + dx);
      h = sh;
      x = sx;
      y = sy;
      break;
    }
    case 'w': {
      w = Math.max(MIN_RESIZE_W, sw - dx);
      h = sh;
      x = sx + sw - w;
      y = sy;
      break;
    }
    default:
      break;
  }

  w = Math.max(MIN_RESIZE_W, w);
  h = Math.max(MIN_RESIZE_H, h);
  return { x, y, width: w, height: h };
}

/** E6-3 API alias: `delta` is cumulative drag from resize pointer-down. */
export function clampBounds(
  startBounds: LayerBounds,
  handle: HandleId,
  delta: { dx: number; dy: number },
  shiftKey: boolean
): LayerBounds {
  return computeResizedBounds(startBounds, handle, delta.dx, delta.dy, shiftKey);
}

/** 1px blue frame + eight 6×6 handle squares (corners + edge midpoints). Export/print: pass drawUI false. */
export function drawSelectionHandles(ctx: CanvasRenderingContext2D, bounds: LayerBounds) {
  // E6-3: hit targets use HANDLE_HIT_PX in hitTestHandle (not the 6px drawn square).
  const HANDLE = 6;
  const h = HANDLE / 2;
  ctx.save();
  ctx.strokeStyle = '#2563eb';
  ctx.lineWidth = 1;
  ctx.strokeRect(bounds.x + 0.5, bounds.y + 0.5, bounds.width - 1, bounds.height - 1);
  ctx.fillStyle = '#ffffff';
  const cx = [
    bounds.x,
    bounds.x + bounds.width / 2,
    bounds.x + bounds.width,
  ];
  const cy = [
    bounds.y,
    bounds.y + bounds.height / 2,
    bounds.y + bounds.height,
  ];
  const pts: Array<[number, number]> = [
    [cx[0], cy[0]],
    [cx[1], cy[0]],
    [cx[2], cy[0]],
    [cx[2], cy[1]],
    [cx[2], cy[2]],
    [cx[1], cy[2]],
    [cx[0], cy[2]],
    [cx[0], cy[1]],
  ];
  for (const [px, py] of pts) {
    ctx.fillRect(px - h, py - h, HANDLE, HANDLE);
    ctx.strokeRect(px - h + 0.5, py - h + 0.5, HANDLE - 1, HANDLE - 1);
  }
  ctx.restore();
}

function layerWithPixelBounds(layer: Layer, b: LayerBounds, sw: number, sh: number): Layer {
  return {
    ...layer,
    x: (b.x / sw) * 100,
    y: (b.y / sh) * 100,
    width: (b.width / sw) * 100,
    height: (b.height / sh) * 100,
  };
}

export interface DrawLayersOptions {
  /** When false, skip selection handles (thumbnails, PDF, presenter). Default false. */
  drawUI?: boolean;
  selectedLayerId?: string | null;
  /** Per-layer drag preview offset in slide logical pixels (not persisted). */
  layerDragOffsetPx?: ReadonlyMap<string, { dx: number; dy: number }> | null;
  /** E6-3: transient resize preview in slide px for one layer. */
  layerResizePreviewPx?: { layerId: string; bounds: LayerBounds } | null;
}

// ─── Public entry point ──────────────────────────────────────────────────────

/**
 * Draw every layer onto the canvas in zIndex order (ascending = back to front).
 * Invisible layers are skipped entirely so they truly contribute nothing to the
 * rasterized output — matches the spec "Hidden layer renders nothing".
 */
export function drawLayers(
  ctx: CanvasRenderingContext2D,
  layers: readonly Layer[],
  slideWidth: number,
  slideHeight: number,
  options?: DrawLayersOptions
) {
  const ordered = [...layers].sort((a, b) => a.zIndex - b.zIndex);
  const offsetMap = options?.layerDragOffsetPx ?? null;
  const resizePx = options?.layerResizePreviewPx ?? null;
  for (const layer of ordered) {
    if (!layer.visible) continue;
    let drawLayer: Layer = layer;
    if (resizePx && resizePx.layerId === layer.id) {
      drawLayer = layerWithPixelBounds(layer, resizePx.bounds, slideWidth, slideHeight);
    } else if (offsetMap?.has(layer.id) === true) {
      drawLayer = layerWithDragOffsetPx(
        layer,
        offsetMap.get(layer.id)!.dx,
        offsetMap.get(layer.id)!.dy,
        slideWidth,
        slideHeight
      );
    }
    if (isTextLayer(drawLayer)) drawTextLayer(ctx, drawLayer, slideWidth, slideHeight);
    else if (isImageLayer(drawLayer)) drawImageLayer(ctx, drawLayer, slideWidth, slideHeight);
    else if (isShapeLayer(drawLayer)) drawShapeLayer(ctx, drawLayer, slideWidth, slideHeight);
  }

  if (options?.drawUI && options.selectedLayerId) {
    const sel = ordered.find((l) => l.id === options.selectedLayerId);
    if (sel?.visible) {
      const drawSelBase =
        offsetMap?.has(sel.id) === true
          ? layerWithDragOffsetPx(
              sel,
              offsetMap.get(sel.id)!.dx,
              offsetMap.get(sel.id)!.dy,
              slideWidth,
              slideHeight
            )
          : sel;
      const handleBounds =
        resizePx?.layerId === sel.id ? resizePx.bounds : getLayerBounds(drawSelBase, slideWidth, slideHeight);
      drawSelectionHandles(ctx, handleBounds);
    }
  }
}
